import api from './api';

class ServiceRatesService {
    /**
     * Get all service rates for the current vendor
     * @param {Object} params - Optional query parameters
     * @returns {Promise} API response
     */
    async getServiceRates(params = {}) {
        try {
            // First get vendor information from locations endpoint
            const vendorResponse = await api.get('/vendors/locations');
            const vendor = vendorResponse.data.vendor;
            
            if (!vendor || !vendor.id) {
                throw new Error('Vendor information not found');
            }
            
            // Then get service rates for this vendor
            const response = await api.get(`/vendors/${vendor.id}/service-rates`, { params });
            return {
                ...response.data,
                vendor: vendor
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get service rate by ID
     * @param {number} rateId - Service rate ID
     * @returns {Promise} API response
     */
    async getServiceRateById(rateId) {
        try {
            // First get vendor information from locations endpoint
            const vendorResponse = await api.get('/vendors/locations');
            const vendor = vendorResponse.data.vendor;
            
            if (!vendor || !vendor.id) {
                throw new Error('Vendor information not found');
            }
            
            // Then get service rate for this vendor
            const response = await api.get(`/vendors/${vendor.id}/service-rates/${rateId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create a new service rate
     * @param {Object} rateData - Service rate data
     * @returns {Promise} API response
     */
    async createServiceRate(rateData) {
        try {
            // First get vendor information from locations endpoint
            const vendorResponse = await api.get('/vendors/locations');
            const vendor = vendorResponse.data.vendor;
            
            if (!vendor || !vendor.id) {
                throw new Error('Vendor information not found');
            }
            
            // Then create service rate for this vendor
            const response = await api.post(`/vendors/${vendor.id}/service-rates`, rateData);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update an existing service rate
     * @param {number} rateId - Service rate ID
     * @param {Object} rateData - Updated service rate data
     * @returns {Promise} API response
     */
    async updateServiceRate(rateId, rateData) {
        try {
            // First get vendor information from locations endpoint
            const vendorResponse = await api.get('/vendors/locations');
            const vendor = vendorResponse.data.vendor;
            
            if (!vendor || !vendor.id) {
                throw new Error('Vendor information not found');
            }
            
            // Then update service rate for this vendor
            const response = await api.put(`/vendors/${vendor.id}/service-rates/${rateId}`, rateData);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete a service rate
     * @param {number} rateId - Service rate ID
     * @returns {Promise} API response
     */
    async deleteServiceRate(rateId) {
        try {
            // First get vendor information from locations endpoint
            const vendorResponse = await api.get('/vendors/locations');
            const vendor = vendorResponse.data.vendor;
            
            if (!vendor || !vendor.id) {
                throw new Error('Vendor information not found');
            }
            
            // Then delete service rate for this vendor
            const response = await api.delete(`/vendors/${vendor.id}/service-rates/${rateId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update service rate status (activate/deactivate)
     * @param {number} rateId - Service rate ID
     * @param {boolean} isActive - New status
     * @returns {Promise} API response
     */
    async updateServiceRateStatus(rateId, isActive) {
        try {
            // Use the update method to change the status
            return await this.updateServiceRate(rateId, { is_active: isActive });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get all service types
     * @returns {Promise} API response
     */
    async getAllServiceTypes() {
        try {
            const response = await api.get('/vendor-registrations/service-types');
            return response.data;
        } catch (error) {
            console.error('Error fetching service types:', error);
            throw error;
        }
    }

    /**
     * Get service subtypes for a specific service type
     * @param {string} serviceTypeCode - Service type code
     * @returns {Promise} API response
     */
    async getServiceSubtypes(serviceTypeCode) {
        try {
            const response = await api.get(`/vendor-registrations/service-types/${serviceTypeCode}/subtypes`);
            return response.data;
        } catch (error) {
            console.error('Error fetching service subtypes:', error);
            throw error;
        }
    }
}

export const serviceRatesService = new ServiceRatesService(); 